import { createApplication } from '@angular/platform-browser';
import { createCustomElement, NgElementConstructor } from '@angular/elements';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { AgentSchedulerCustomElementComponent } from '../../custom-elements/agent-scheduler.component';
import { BatteryQuoterCustomElementComponent } from '../../custom-elements/battery-quoter.component';
import { MyAccountCtaCustomElementComponent } from '../../custom-elements/my-account-cta.component';
import { OfficeAgentsCustomElementComponent } from '../../custom-elements/office-agents.component';
import { AccountEmailSyncCustomElementComponent } from '../../custom-elements/account-email-sync.component';
import { FileManagerCustomElementComponent } from '../../custom-elements/file-manager.component';
import { NotificationCustomElementComponent } from '../../custom-elements/notification.component';
import { SmartWeatherCustomElementComponent } from '../../custom-elements/smart-weather.component';
import { AvaEmemberCustomElementComponent } from '../../custom-elements/ava-emember.component';
import { appConfig } from '../app.config';
import { environment } from '../../../environments/environment';
import { ClubApp } from '../../types';

registerLocaleData(en);

function defineCustomElement(elementName: string, element: NgElementConstructor<unknown> | CustomElementConstructor) {
  if (customElements.get(elementName) === undefined) {
    customElements.define(elementName, element);
  }
}

export async function buildCustomElement() {
  const app = await createApplication(appConfig);
  /**
   * Create the custom element from an Angular component.
   */
  const agentSchedulerElement = createCustomElement(AgentSchedulerCustomElementComponent, {
    injector: app.injector,
  });
  const batteryElement = createCustomElement(BatteryQuoterCustomElementComponent, { injector: app.injector });
  const myAccountCtaElement = createCustomElement(MyAccountCtaCustomElementComponent, { injector: app.injector });
  const ememberElement = createCustomElement(AvaEmemberCustomElementComponent, {
    injector: app.injector,
  });
  const officeAgentsElement = createCustomElement(OfficeAgentsCustomElementComponent, { injector: app.injector });

  const accountEmailSyncElement = createCustomElement(AccountEmailSyncCustomElementComponent, {
    injector: app.injector,
  });
  const fileManagerElement = createCustomElement(FileManagerCustomElementComponent, { injector: app.injector });
  const notificationElement = createCustomElement(NotificationCustomElementComponent, { injector: app.injector });
  const smartWeatherElement = createCustomElement(SmartWeatherCustomElementComponent, { injector: app.injector });

  /**
   * Define the custom element with a name.
   */
  defineCustomElement('ava-agent-scheduler-custom-element', agentSchedulerElement);
  defineCustomElement('ava-battery-quoter-custom-element', batteryElement);
  defineCustomElement('ava-emember-custom-element', ememberElement);
  defineCustomElement('ava-my-account-cta-custom-element', myAccountCtaElement);
  defineCustomElement('ava-office-agents-custom-element', officeAgentsElement);
  defineCustomElement('ava-smart-weather-custom-element', smartWeatherElement);
  defineCustomElement('ava-file-manager-custom-element', fileManagerElement);
  defineCustomElement('ava-account-email-sync-custom-element', accountEmailSyncElement);
  defineCustomElement('ava-notification-custom-element', notificationElement);

  /**
   * Northway's Drupal account page carries the retired multiblock tag (removed in #560) and can't be
   * edited, so on Northway that tag runs the emember app. The registry won't take one constructor
   * under two names, hence the second createCustomElement. Drupal renders that tag on every page, so it
   * is only defined when the URL carries ?app=; on other pages it stays an inert unknown element.
   */
  if (environment.clubId === ClubApp.Northway && new URLSearchParams(window.location.search).get('app')) {
    const northwayMultiblockElement = createCustomElement(AvaEmemberCustomElementComponent, {
      injector: app.injector,
    });
    defineCustomElement('ava-multiblock-custom-element', northwayMultiblockElement);
  }
}
